import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'
import { Box, Chip, Typography } from '@mui/material'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import InputBase from '@mui/material/InputBase'
import { CustomModalContext } from 'common/contexts/CustomModalContext'
import { useContext, useEffect, useRef, useState } from 'react'
import SelectorConBuscador from './SelectorConBuscador'

const LovField = ({
  campo,
  expediente,
  placeholder,
  onValueChange,
  value,
  filtros = [],
  error,
}) => {
  const multiple = campo?.tipo_lov === 'M'

  const { handleModal } = useContext(CustomModalContext)

  const [seleccion, setSeleccion] = useState(multiple ? [] : null)
  const [busqueda, setSearch] = useState('')
  const [committedSearch, setCommittedSearch] = useState('')
  const [searchToken, setSearchToken] = useState(0)

  const inputRef = useRef(null)

  useEffect(() => {
    if (value === undefined) return
    if (multiple) {
      const arr = Array.isArray(value) ? value : []
      setSeleccion(arr.map((v) => ({ value: v, label: String(v) })))
    } else {
      setSeleccion(value ? { value, label: String(value) } : null)
    }
  }, [value, multiple])

  const cerrarModal = () => handleModal({ enabled: false })

  const manejarCambio = (val) => {
    setSeleccion(val)
    if (multiple) {
      onValueChange?.(val.map((item) => item.value))
    } else {
      onValueChange?.(val?.value ?? '')
    }
    setSearch('')
  }

  const eliminarSeleccion = (valor) => {
    if (!multiple) return
    const nuevaSeleccion = (seleccion || []).filter((item) => item.value !== valor)
    setSeleccion(nuevaSeleccion)
    onValueChange?.(nuevaSeleccion.map((item) => item.value))
  }

  const clearAll = () => {
    setSeleccion(multiple ? [] : null)
    onValueChange?.(multiple ? [] : '')
  }

  const renderLabel = (item) => {
    if (!item) return ''
    if (item.descripcionLoV) {
      return `${item.value} - ${item.descripcionLoV}`
    }

    return item.label ?? item.descripcion ?? item.nombre ?? item.value
  }

  useEffect(() => {
    const committed = busqueda.trim()
    setCommittedSearch(committed)
  }, [busqueda])

  const openWithSearch = (e) => {
    setSearchToken((t) => t + 1)
    openModal(true)
  }

  const hasSelection = multiple ? (seleccion?.length ?? 0) > 0 : !!seleccion
  const showInput = !hasSelection

  const openModal = () => {
    handleModal({
      closable: true,
      content: (
        <SelectorConBuscador
          key={`${campo.codigo_lov}-${searchToken}`}
          open={true}
          onClose={cerrarModal}
          campo={campo.columna}
          codigo_lv={campo.codigo_lov}
          expediente={expediente}
          multiple={multiple}
          onChange={manejarCambio}
          placeholder={campo.placeholder}
          value={multiple ? (seleccion?.map((item) => item.value) ?? []) : (seleccion?.value ?? '')}
          outSearch={committedSearch}
          filtros={filtros}
        />
      ),
    })
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Box sx={{ display: 'flex' }}>
        <Typography variant="h3" color="primary" sx={{ fontWeight: 500, fontSize: '1rem' }}>
          {campo.literal}
        </Typography>
        {error && (
          <Typography
            variant="h3"
            sx={{ color: 'error.main', fontWeight: 400, fontSize: '0.8rem', ml: 1 }}
          >
            {'* ' + error}
          </Typography>
        )}
      </Box>

      <Box
        role="group"
        aria-label={`${campo.literal || 'Buscador'} campo LOV`}
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1,
          fontWeight: '400',
          color: 'text.primary',
          backgroundColor: '#fff',
          zIndex: 0,
          minHeight: '48px',
          height: 'auto',
          borderRadius: 4,
          borderBottom: 'none',
          boxShadow: 'none',
          px: 1,
          border: error ? '1px solid red' : 'none',
        }}
        onMouseDown={(e) => {
          const target = e.target
          const isChipDelete = target.getAttribute?.('aria-label') === 'Delete'
          if (!isChipDelete) inputRef.current?.focus()
        }}
      >
        {multiple ? (
          (seleccion?.length ?? 0) > 0 &&
          seleccion.map((item) => (
            <Chip
              key={item.value}
              label={renderLabel(item)}
              onDelete={(e) => {
                e.stopPropagation()
                eliminarSeleccion(item.value)
              }}
              sx={{ bgcolor: '#e3f2fd' }}
            />
          ))
        ) : (
          <>{seleccion && <Typography sx={{ mr: 1 }}>{renderLabel(seleccion)}</Typography>}</>
        )}

        {showInput ? (
          <InputBase
            inputRef={inputRef}
            value={busqueda}
            disabled={campo.desactivado === 'S'}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === 'Tab') {
                e.preventDefault()
                openWithSearch(e)
              }
            }}
            placeholder={!multiple && renderLabel(seleccion) ? '' : placeholder || 'Buscar...'}
            sx={{
              flexGrow: 1,
              minWidth: 120,
              py: 0.5,
            }}
            inputProps={{ 'aria-label': 'Buscar en LOV' }}
            endAdornment={
              <InputAdornment position="end" sx={{ ml: 'auto' }}>
                <IconButton
                  edge="end"
                  sx={{ color: '#1976d2' }}
                  disabled={campo.desactivado === 'S'}
                  onClick={openWithSearch}
                  aria-label="Abrir buscador"
                >
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            }
          />
        ) : (
          <>
            {hasSelection && (
              <IconButton
                edge="end"
                size="small"
                sx={{ color: 'gray' }}
                disabled={campo.desactivado === 'S'}
                onClick={clearAll}
                aria-label="Limpiar selección"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
            <IconButton
              edge="end"
              sx={{ color: '#1976d2', ml: 'auto' }}
              onClick={openWithSearch}
              aria-label="Abrir buscador"
            >
              <SearchIcon />
            </IconButton>
          </>
        )}
      </Box>
    </Box>
  )
}

export default LovField
